/**
 * The way in: a link to sign-up that says "Start free".
 *
 * A plain anchor, not a router link. The public header carries it onto the
 * static pages, where there is no router, and one element that works in both
 * places cannot drift into two. Each one reports its own click with where it
 * sat, which is the one number the database cannot give.
 *
 * `data-cta-placement`, not `data-signup-cta`: the static pages mark their
 * hand-written anchors with the second and count them with a script of their
 * own, and a button carrying both would be counted twice.
 */
import { ArrowRight } from 'lucide-react'
import { cn } from '@/utils/cn'
import { trackSignupCtaClick } from '@/utils/signupCta'
import '@/design-system/components.css'

export const START_FREE_LABEL = 'Start free'
export const START_FREE_HREF = '/sign-up'
/* The one line beside the button, wherever a page prints one. It names the
   paid HubSpot plan, as every promise of a fix on a page must. */
export const START_FREE_LINE = 'A free account. Your first page fixed free, with no card. It needs a paid HubSpot plan.'

interface StartFreeProps {
  /** Where this button sits, for the click count: home_hero, header, and so on. */
  placement: string
  /** Ink by default. The paper button where it sits on a coloured band. */
  variant?: 'primary' | 'secondary'
  size?: 'sm' | 'md' | 'lg'
  /** The arrow after the words. Left off in the header, where the row is tight. */
  arrow?: boolean
  className?: string
}

export default function StartFree({ placement, variant = 'primary', size = 'lg', arrow = true, className }: StartFreeProps) {
  return (
    <a
      href={START_FREE_HREF}
      className={cn('ds-btn', `ds-btn--${variant}`, size !== 'md' && `ds-btn--${size}`, className)}
      data-cta-placement={placement}
      onClick={() => trackSignupCtaClick(placement)}
    >
      {START_FREE_LABEL}
      {arrow ? <ArrowRight aria-hidden="true" /> : null}
    </a>
  )
}
